/**
 * Lightweight client-side analytics — fires a beacon to /api/analytics/track
 * on every route change and exposes a manual `trackEvent` helper for things
 * like "tool launched" or button-level conversion events.
 *
 * Anonymous visitor_id is stored in localStorage so we can stitch sessions
 * together over time. When the user is logged in their JWT is sent and the
 * backend will associate the event with their user_id.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { API } from "@/api";

const VISITOR_KEY = "vbs_visitor_id";

function getVisitorId() {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || ("v_" + Math.random().toString(36).slice(2) + Date.now().toString(36));
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

function readUtm(search) {
  const sp = new URLSearchParams(search || "");
  return {
    utm_source: sp.get("utm_source") || "",
    utm_medium: sp.get("utm_medium") || "",
    utm_campaign: sp.get("utm_campaign") || "",
    utm_term: sp.get("utm_term") || "",
    utm_content: sp.get("utm_content") || "",
  };
}

export async function trackEvent(payload) {
  try {
    const token = localStorage.getItem("vbs_token");
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const body = {
      visitor_id: getVisitorId(),
      page_title: document.title || "",
      referrer: document.referrer || "",
      ...payload,
    };
    // Fire-and-forget — never block the UI on telemetry
    await fetch(`${API}/analytics/track`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch (_) {
    /* silent */
  }
}

export default function useAnalytics() {
  const loc = useLocation();
  useEffect(() => {
    // Don't track admin self-traffic — keeps the top-pages chart honest.
    if (loc.pathname.startsWith("/admin")) return;
    // Cache last-tracked path so a re-render or HMR doesn't double-count
    const key = loc.pathname + loc.search;
    if (window.__vbsLastPath === key) return;
    window.__vbsLastPath = key;
    trackEvent({
      type: "pageview",
      path: loc.pathname,
      ...readUtm(loc.search),
    });
  }, [loc.pathname, loc.search]);
}
